import { find, findIndex } from 'lodash';
import {
  OFFICER_LIST_REQUEST,
  OFFICER_LIST_REQUEST_ERROR,
  OFFICER_LIST_REQUEST_SUCCESS,
  OFFICER_ADD,
  OFFICER_ADD_ERROR,
  OFFICER_ADD_SUCCESS,
  OFFICER_REQUEST,
  OFFICER_REQUEST_SUCCESS,
  OFFICER_REQUEST_ERROR,
  OFFICER_REMOVE,
  OFFICER_REMOVE_ERROR,
  OFFICER_REMOVE_SUCCESS,
  OFFICER_UPDATE,
  OFFICER_UPDATE_ERROR,
  OFFICER_UPDATE_SUCCESS,
  OFFICER_PERFORMANCE_LIST_REQUEST,
  OFFICER_PERFORMANCE_LIST_REQUEST_ERROR,
  OFFICER_PERFORMANCE_LIST_REQUEST_SUCCESS,
} from '@/store/actions/officer';
import { handleRequestError } from '@/utils/error';

import Repository, { OFFICERS } from '@/repositories/RepositoryFactory';

const OfficersRepository = Repository.get(OFFICERS);

const state = {
  officer: '',
  officerList: [],
  officerLastCreated: '',
  officerMeta: '',
  officerStatus: '',
  officerPerformanceList: [],
  officerPerformanceListStatus: '',
  officerPerformanceListMeta: '',
};

const getters = {
  getOfficerById: (state, getters) => id => {
    if (getters.officerList.length > 0) {
      return find(getters.officerList, { id });
    }

    return state.service;
  },
  officer: state => state.officer,
  officerList: state => state.officerList,
  officerLastCreated: state => state.officerLastCreated,
  officerMeta: state => state.officerMeta,
  officerStatus: state => state.officerStatus,
  officerPerformanceList: state => state.officerPerformanceList,
  officerPerformanceListStatus: state => state.officerPerformanceListStatus,
  officerPerformanceListMeta: state => state.officerPerformanceListMeta,
};

const actions = {
  [OFFICER_LIST_REQUEST]: async ({ commit }) => {
    commit(OFFICER_LIST_REQUEST);
    try {
      const response = await OfficersRepository.get();
      commit(OFFICER_LIST_REQUEST_SUCCESS, response.data);
    } catch (error) {
      commit(OFFICER_LIST_REQUEST_ERROR);
      handleRequestError(error);
    }
  },

  [OFFICER_REQUEST]: async ({ commit }, payload) => {
    commit(OFFICER_REQUEST);
    try {
      const response = await OfficersRepository.getSelf(payload);
      const { data } = response;
      commit(OFFICER_REQUEST_SUCCESS, data);
    } catch (error) {
      commit(OFFICER_REQUEST_ERROR);
      handleRequestError(error);
    }
  },

  [OFFICER_ADD]: async ({ commit }, payload) => {
    commit(OFFICER_ADD);
    try {
      const response = await OfficersRepository.create(payload.businessId, payload.data);
      commit(OFFICER_ADD_SUCCESS, response.data.data);
    } catch (error) {
      commit(OFFICER_ADD_ERROR);
      handleRequestError(error);
    }
  },

  [OFFICER_UPDATE]: async ({ commit }, payload) => {
    commit(OFFICER_UPDATE);
    try {
      const response = await OfficersRepository.update(
        payload.businessId,
        payload.id,
        payload.data
      );
      commit(OFFICER_UPDATE_SUCCESS, response.data.data);
    } catch (error) {
      commit(OFFICER_UPDATE_ERROR);
      handleRequestError(error);
    }
  },

  [OFFICER_REMOVE]: async ({ commit }, payload) => {
    commit(OFFICER_REMOVE);
    try {
      await OfficersRepository.delete(payload.businessId, payload.id);
      commit(OFFICER_REMOVE_SUCCESS, payload.id);
    } catch (error) {
      commit(OFFICER_REMOVE_ERROR);
      handleRequestError(error);
    }
  },

  [OFFICER_PERFORMANCE_LIST_REQUEST]: async ({ commit }, payload) => {
    commit(OFFICER_PERFORMANCE_LIST_REQUEST);
    try {
      const response = await OfficersRepository.getPerformances(payload);
      commit(OFFICER_PERFORMANCE_LIST_REQUEST_SUCCESS, response.data);
    } catch (error) {
      commit(OFFICER_PERFORMANCE_LIST_REQUEST_ERROR);
      handleRequestError(error);
    }
  },
};

const mutations = {
  [OFFICER_LIST_REQUEST]: state => {
    state.officerStatus = 'loading';
  },
  [OFFICER_LIST_REQUEST_SUCCESS]: (state, payload) => {
    state.officerStatus = 'success';
    state.officerList = payload.data;
    state.officerMeta = payload.meta;
  },
  [OFFICER_LIST_REQUEST_ERROR]: state => {
    state.officerStatus = 'error';
  },
  [OFFICER_ADD]: state => {
    state.officerStatus = 'loading';
    state.officerLastCreated = '';
  },
  [OFFICER_ADD_SUCCESS]: (state, payload) => {
    state.officerStatus = 'success';
    state.officerLastCreated = payload;
    state.officerList.push(payload);
  },
  [OFFICER_ADD_ERROR]: state => {
    state.officerStatus = 'error';
  },
  [OFFICER_REQUEST]: state => {
    state.officerStatus = 'loading';
  },
  [OFFICER_REQUEST_SUCCESS]: (state, payload) => {
    state.officerStatus = 'success';
    state.officer = payload.data;
  },
  [OFFICER_REQUEST_ERROR]: state => {
    state.officerStatus = 'error';
  },
  [OFFICER_UPDATE]: state => {
    state.officerStatus = 'loading';
  },
  [OFFICER_UPDATE_SUCCESS]: (state, payload) => {
    state.officerStatus = 'success';
    const index = findIndex(state.officerList, { id: payload.id });
    state.officerList[index] = payload;
  },
  [OFFICER_UPDATE_ERROR]: state => {
    state.officerStatus = 'error';
  },
  [OFFICER_REMOVE]: state => {
    state.officerStatus = 'deleting';
  },
  [OFFICER_REMOVE_SUCCESS]: (state, payload) => {
    const index = findIndex(state.officerList, { id: payload });
    state.officerList.splice(index, 1);
  },
  [OFFICER_REMOVE_ERROR]: state => {
    state.officerStatus = 'error';
  },
  [OFFICER_PERFORMANCE_LIST_REQUEST]: state => {
    state.officerPerformanceListStatus = 'loading';
  },
  [OFFICER_PERFORMANCE_LIST_REQUEST_SUCCESS]: (state, payload) => {
    state.officerPerformanceListStatus = 'success';
    state.officerPerformanceList = payload.data;
    if (payload.meta) {
      state.officerPerformanceListMeta = payload.meta;
    }
  },
  [OFFICER_PERFORMANCE_LIST_REQUEST_ERROR]: state => {
    state.officerPerformanceListStatus = 'error';
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
